import type { ZohoStepDTO } from "../data/zohoConnectApi";

/**
 * The go-live sequence folded into four stages for the screen.
 *
 * The server reports one list: the Zoho books steps, or the Tally copy steps,
 * never both. A failure still names the exact step that broke. The screen
 * folds that list into four stages, each with a live line saying what is
 * happening inside it right now, and the full rows one click away (or forced
 * open, on failure).
 *
 * Grouping is sticky by server order: a step whose key this map does not know
 * (a step added later) lands in the same stage as the step before it rather
 * than vanishing or crashing the screen.
 */

export interface GoLiveGroup {
  id: string;
  title: string;
  steps: ZohoStepDTO[];
}

const GROUP_TITLE: Record<string, string> = {
  workspace: "Creating the workspace",
  team: "Adding the team & rules",
  books: "Bringing the books over",
  live: "Going live",
};

const GROUP_OF: Record<string, string> = {
  subdomain: "workspace",
  provision: "workspace",
  seeds: "workspace",
  company: "workspace",
  "zoho-token-binding": "workspace",
  "team-roles": "team",
  team: "team",
  checkers: "team",
  escalations: "team",
  checklist: "team",
  token: "books",
  taxes: "books",
  "tax-exemption": "books",
  "pull-currencies": "books",
  "pull-taxes": "books",
  "pull-locations": "books",
  "pull-ledgers": "books",
  "no-bill": "books",
  "rounding-off": "books",
  "tds-accounts": "books",
  contacts: "books",
  banks: "books",
  "pull-books": "books",
  "pull-outstanding": "books",
  "tally-catalogues": "books",
  "tally-chart": "books",
  "tally-parties": "books",
  "tally-extras": "books",
  live: "live",
};

export function groupGoLiveSteps(steps: ZohoStepDTO[]): GoLiveGroup[] {
  const groups: GoLiveGroup[] = [];
  let currentId = "workspace";

  for (const step of steps) {
    currentId = GROUP_OF[step.key] ?? currentId;
    const last = groups[groups.length - 1];

    if (last !== undefined && last.id === currentId) {
      last.steps.push(step);
    } else {
      groups.push({ id: currentId, title: GROUP_TITLE[currentId] ?? currentId, steps: [step] });
    }
  }

  return groups;
}

/** A stage is exactly as far along as its worst step. */
export function groupStatus(group: GoLiveGroup): "pending" | "running" | "done" | "failed" {
  if (group.steps.some((step) => step.status === "failed")) return "failed";
  if (group.steps.every((step) => step.status === "done")) return "done";
  if (group.steps.some((step) => step.status !== "pending")) return "running";

  return "pending";
}
